import ky from 'ky';

const DEFAULTS = {
  timeout: 25000,
  retry: { methods: ['get'] },
};

const readBody = async (resp) => {
  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('json')) return resp.json();
  if (ct.includes('text') || ct.includes('xml')) return resp.text();
  return new Uint8Array(await resp.arrayBuffer());
};

const normalizeResponse = async (promise) => {
  try {
    const resp = await promise;
    return { data: await readBody(resp), status: resp.status };
  } catch (err) {
    if (err.response) {
      // ky v2: err.data has pre-parsed body, assign for axios compat
      err.response.data = err.data || null;
      err.response.message = err.message;
    } else {
      // Network/timeout errors: add .request for axios compat (callers check e.request)
      err.request = true;
      if (!err.code) {
        err.code = err.name === 'TimeoutError' ? 'TIMEOUT' : 'NETWORK_ERROR';
      }
    }
    throw err;
  }
};

const withOptions = (opts = {}) => {
  const { headers, params, searchParams, withCredentials, ...rest } = opts;
  const o = { ...rest };
  if (params !== undefined) o.searchParams = params;
  else if (searchParams !== undefined) o.searchParams = searchParams;
  if (withCredentials) o.credentials = 'include';
  if (headers) o.headers = headers;
  return o;
};

const withBody = (body, opts = {}) => {
  const o = withOptions(opts);
  if (body instanceof FormData) {
    o.body = body;
    // ky v2 mergeHeaders: when source2 is a Headers instance and value === 'undefined',
    // it deletes that key from the merged result BEFORE Request construction.
    // This removes instance-default 'Content-type: application/json' and any caller-set
    // 'Content-Type: multipart/form-data' (which lacks boundary).
    // Browser then auto-sets multipart/form-data with correct boundary.
    const h = new Headers(o.headers || {});
    h.set('content-type', 'undefined');
    o.headers = h;
    // Disable retry to prevent Request.clone() which converts FormData body
    // to ReadableStream (tee) — making payload invisible in Chrome DevTools.
    o.retry = 0;
  } else if (body !== undefined) {
    o.json = body;
  }
  return o;
};

export const createInstant = (config = {}) => {
  const inst = ky.create({ ...DEFAULTS, ...config });
  return {
    get:    (url, opts)       => normalizeResponse(inst.get(url, withOptions(opts))),
    post:   (url, body, opts) => normalizeResponse(inst.post(url, withBody(body, opts))),
    put:    (url, body, opts) => normalizeResponse(inst.put(url, withBody(body, opts))),
    patch:  (url, body, opts) => normalizeResponse(inst.patch(url, withBody(body, opts))),
    delete: (url, opts)       => normalizeResponse(inst.delete(url, withOptions(opts))),
    head:   (url, opts)       => normalizeResponse(inst.head(url, withOptions(opts))),
  };
};

export default ky;
